import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articleDetail, defaultArticleDetail, relatedArticles } from '@/mocks/article';
import { categories } from '@/mocks/home';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();
  const [chapterOrder, setChapterOrder] = useState<'asc' | 'desc'>('desc');
  const [chapterPage, setChapterPage] = useState(1);
  const chaptersPerPage = 20;

  const article = (slug && articleDetail[slug]) ? articleDetail[slug] : defaultArticleDetail;

  const catMeta = categories.find((c) => c.slug === article.categorySlug);

  const sortedChapters = chapterOrder === 'asc'
    ? [...article.chapterList].reverse()
    : article.chapterList;

  const totalChapterPages = Math.ceil(sortedChapters.length / chaptersPerPage);
  const pagedChapters = sortedChapters.slice(
    (chapterPage - 1) * chaptersPerPage,
    chapterPage * chaptersPerPage
  );

  return (
    <>
      <Navbar />
      <main>
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 pt-20 md:pt-24">
          <Breadcrumb
            items={[
              { label: t('article:breadcrumb_home'), path: '/' },
              { label: t('article:breadcrumb_category'), path: `/category/${article.categorySlug}` },
              { label: article.category },
              { label: article.title },
            ]}
          />
        </div>

        <section className="max-w-[1280px] mx-auto px-4 md:px-6 pb-14 md:pb-20">
          <div className="flex flex-col lg:flex-row gap-8 md:gap-12">
            <div className="lg:w-[300px] flex-shrink-0 flex flex-col sm:flex-row lg:flex-col gap-6">
              <div className="w-[200px] sm:w-[220px] lg:w-[260px] mx-auto lg:mx-0 aspect-[3/4] rounded-2xl overflow-hidden flex-shrink-0">
                <img
                  src={article.coverImage}
                  alt={article.title}
                  title={`${article.title} - ${article.author}`}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex px-2.5 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-background-100 rounded-xl p-3">
                    <p className="text-xs text-foreground-500">{t('article:author')}</p>
                    <p className="text-sm font-semibold text-foreground-950 mt-0.5">{article.author}</p>
                  </div>
                  <div className="bg-background-100 rounded-xl p-3">
                    <p className="text-xs text-foreground-500">{t('article:chapters_count', { count: article.chapters })}</p>
                    <p className="text-sm font-semibold text-foreground-950 mt-0.5">
                      <span className="text-accent-600">{article.status === '连载中' ? t('article:status_ongoing') : t('article:status_completed')}</span>
                    </p>
                  </div>
                  <div className="bg-background-100 rounded-xl p-3">
                    <p className="text-xs text-foreground-500">{t('article:views')}</p>
                    <p className="text-sm font-semibold text-foreground-950 mt-0.5">{article.views}</p>
                  </div>
                  <div className="bg-background-100 rounded-xl p-3">
                    <p className="text-xs text-foreground-500">{t('article:rating')}</p>
                    <p className="text-sm font-semibold text-accent-600 mt-0.5">{article.rating}</p>
                  </div>
                </div>

                <Link
                  to={`/article/${article.id}`}
                  className="mt-5 flex items-center justify-center gap-2 w-full py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-play-circle-line text-base"></i>
                  </span>
                  {t('article:start_reading')}
                </Link>
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-1">{article.title}</h1>
              <p className="text-sm text-foreground-500 mb-6">
                {article.status} · {t('article:last_updated')} {article.lastUpdated}
              </p>

              <div className="mb-8 md:mb-10">
                <h2 className="text-sm font-semibold text-foreground-950 mb-3">{t('article:description')}</h2>
                <div className="text-sm text-foreground-600 leading-relaxed space-y-3">
                  {article.longDescription.split('\n\n').map((para, idx) => (
                    <p key={idx}>{para}</p>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-sm font-semibold text-foreground-950">{t('article:chapter_list')}</h2>
                  <button
                    onClick={() => {
                      setChapterOrder(chapterOrder === 'asc' ? 'desc' : 'asc');
                      setChapterPage(1);
                    }}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-background-100 text-xs font-medium text-foreground-600 hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className={`text-sm ${chapterOrder === 'asc' ? 'ri-sort-asc' : 'ri-sort-desc'}`}></i>
                    </span>
                    {chapterOrder === 'asc' ? t('article:chapter_sort_asc') : t('article:chapter_sort_desc')}
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {pagedChapters.map((ch) => (
                    <Link
                      key={ch.number}
                      to={`/article/${article.id}`}
                      className="flex items-center justify-between px-4 py-3 rounded-xl bg-background-50 border border-background-200/70 hover:border-accent-300 hover:bg-background-100 transition-all duration-200 cursor-pointer"
                    >
                      <span className="text-sm text-foreground-950 font-medium truncate">{ch.title}</span>
                      <span className="text-xs text-foreground-400 ml-2 flex-shrink-0">{ch.date}</span>
                    </Link>
                  ))}
                </div>

                {totalChapterPages > 1 && (
                  <div className="flex items-center justify-center gap-1.5 mt-6">
                    {Array.from({ length: totalChapterPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => setChapterPage(page)}
                        className={`w-8 h-8 rounded-full text-xs font-medium transition-all duration-200 cursor-pointer ${
                          page === chapterPage
                            ? 'bg-primary-500 text-background-50'
                            : 'text-foreground-600 hover:bg-background-100'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        <section className="bg-background-100/60 py-14 md:py-20">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-1 h-6 rounded-full bg-accent-500"></div>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">{t('article:related_comics')}</h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {relatedArticles.map((comic) => (
                <Link
                  key={comic.id}
                  to={`/article/${comic.id}`}
                  className="group bg-background-50 rounded-2xl overflow-hidden border border-background-200/70 hover:border-accent-300 transition-all duration-200 cursor-pointer"
                >
                  <div className="aspect-[3/4] overflow-hidden">
                    <img
                      src={comic.coverImage}
                      alt={comic.title}
                      title={`${comic.title} - ${comic.author}`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-400"
                    />
                  </div>
                  <div className="p-3">
                    <h3 className="text-sm font-semibold text-foreground-950 truncate">{comic.title}</h3>
                    <div className="flex items-center justify-between mt-1.5">
                      <span className="text-xs text-foreground-500">{comic.author}</span>
                      <span className="text-xs text-accent-600 font-medium">{comic.rating}{t('article:rating')}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'ComicSeries',
              name: article.title,
              author: {
                '@type': 'Person',
                name: article.author,
              },
              description: article.longDescription,
              url: `http://591chaxin.cn/article/${article.id}`,
              numberOfEpisodes: article.chapters,
              datePublished: '2018-06-01',
              inLanguage: 'zh-CN',
              genre: article.category,
              aggregateRating: {
                '@type': 'AggregateRating',
                ratingValue: String(article.rating),
                bestRating: '10',
                ratingCount: parseInt(article.views.replace('万', '0000'), 10),
              },
              breadcrumb: {
                '@type': 'BreadcrumbList',
                itemListElement: [
                  { '@type': 'ListItem', position: 1, name: '首页', item: 'http://591chaxin.cn/' },
                  { '@type': 'ListItem', position: 2, name: article.category, item: `http://591chaxin.cn/category/${article.categorySlug}` },
                  { '@type': 'ListItem', position: 3, name: article.title },
                ],
              },
            }),
          }}
        />
      </main>
      <Footer />
    </>
  );
}